import { CRE_EXTENSION_PATH } from "../constants/routes";

const RETURN_ROUTE_KEY = "cre_medmic_return_route";

/** Allowlisted MedMic post-login destinations (path only). */
export const MEDMIC_RETURN_ROUTES = Object.freeze([
  CRE_EXTENSION_PATH,
  `${CRE_EXTENSION_PATH}/handoff`,
]);

const ALLOWED = new Set(MEDMIC_RETURN_ROUTES);

/**
 * Normalize a candidate to an allowlisted MedMic path, or null if unsafe.
 * Rejects absolute URLs, protocol-relative URLs, query/hash smuggling, and
 * any path outside the MedMic allowlist.
 *
 * @param {unknown} candidate
 * @returns {string | null}
 */
export function normalizeMedmicReturnRoute(candidate) {
  if (typeof candidate !== "string") return null;

  const trimmed = candidate.trim();
  if (!trimmed) return null;

  // Reject absolute / protocol-relative / backslash open-redirect forms.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) return null;
  if (trimmed.startsWith("//") || trimmed.includes("\\")) return null;

  let pathname = trimmed;
  try {
    const q = pathname.indexOf("?");
    const h = pathname.indexOf("#");
    const cut = Math.min(
      q === -1 ? pathname.length : q,
      h === -1 ? pathname.length : h,
    );
    pathname = pathname.slice(0, cut);
  } catch {
    return null;
  }

  // Collapse trailing slash except for root (not allowlisted anyway).
  if (pathname.length > 1 && pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1);
  }

  if (!pathname.startsWith("/") || pathname.includes("//")) return null;
  if (!ALLOWED.has(pathname)) return null;
  return pathname;
}

/**
 * Persist the originating MedMic route before interactive MSAL login.
 * @param {string} [pathname]
 * @returns {boolean} whether a value was stored
 */
export function setMedmicReturnRoute(pathname) {
  const path =
    pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "");
  const safe = normalizeMedmicReturnRoute(path);
  if (!safe) return false;
  try {
    sessionStorage.setItem(RETURN_ROUTE_KEY, safe);
    return true;
  } catch {
    return false;
  }
}

/** @returns {string | null} */
export function peekMedmicReturnRoute() {
  try {
    return normalizeMedmicReturnRoute(sessionStorage.getItem(RETURN_ROUTE_KEY));
  } catch {
    return null;
  }
}

/**
 * Read and clear the stored return route (only allowlisted values).
 * @returns {string | null}
 */
export function consumeMedmicReturnRoute() {
  try {
    const raw = sessionStorage.getItem(RETURN_ROUTE_KEY);
    sessionStorage.removeItem(RETURN_ROUTE_KEY);
    return normalizeMedmicReturnRoute(raw);
  } catch {
    return null;
  }
}

export function clearMedmicReturnRoute() {
  try {
    sessionStorage.removeItem(RETURN_ROUTE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Resolve the post-login MedMic path: stored allowlisted route, else default gate.
 * Clears storage when a stored value is present (valid or not).
 * @returns {string}
 */
export function resolveMedmicReturnRoute() {
  const stored = consumeMedmicReturnRoute();
  return stored ?? CRE_EXTENSION_PATH;
}
